import { io, type Socket } from 'socket.io-client';
import { getValidToken, isBffMode, isSessionAlive } from '@/features/auth';
import { config } from '@/shared/lib/config';

let socket: Socket | null = null;
let connecting: Promise<Socket | null> | null = null;
let refCount = 0;
let generation = 0;
const ticketRooms = new Map<string, number>();
const sprintGroupRooms = new Map<string, number>();
const chatRooms = new Map<string, number>();

async function createSocket(): Promise<Socket | null> {
  if (!config.wsUrl) return null;
  const bff = isBffMode();
  const token = await getValidToken();
  if (!token && !(bff && isSessionAlive())) return null;
  const s = io(config.wsUrl, {
    auth: (cb) => {
      if (bff) return cb({});
      void getValidToken().then((t) => cb({ token: t ?? '' }));
    },
    transports: ['websocket', 'polling'],
  });
  s.on('connect', () => {
    for (const id of ticketRooms.keys()) s.emit('join_ticket', id);
    for (const id of sprintGroupRooms.keys()) s.emit('join_sprint_group', id);
    for (const id of chatRooms.keys()) s.emit('join_chat', id);
  });
  s.on('auth:refresh', (ack?: (token: string) => void) => {
    if (typeof ack !== 'function') return;
    void getValidToken().then((t) => ack(t ?? ''));
  });
  s.on('disconnect', (reason) => {
    if (reason !== 'io server disconnect') return;
    scheduleServerDisconnectRecovery(s);
  });
  return s;
}

const NO_TOKEN_RETRY_DELAYS_MS = [1000, 3000, 8000];

async function createSocketWithRetry(gen: number): Promise<Socket | null> {
  for (let attempt = 0; ; attempt += 1) {
    const s = await createSocket();
    if (s) return s;
    if (!config.wsUrl || attempt >= NO_TOKEN_RETRY_DELAYS_MS.length) return null;
    if (gen !== generation || refCount === 0) return null;
    await new Promise<void>((resolve) => setTimeout(resolve, NO_TOKEN_RETRY_DELAYS_MS[attempt]));
    if (gen !== generation || refCount === 0) return null;
  }
}

function scheduleServerDisconnectRecovery(s: Socket): void {
  const reconnect = () => {
    if (s !== socket || refCount === 0 || s.connected) return;
    void getValidToken().then((t) => {
      if ((t || (isBffMode() && isSessionAlive())) && s === socket && !s.connected) s.connect();
    });
  };
  if (document.visibilityState === 'visible') {
    reconnect();
    return;
  }
  const onVisible = () => {
    if (document.visibilityState !== 'visible') return;
    document.removeEventListener('visibilitychange', onVisible);
    reconnect();
  };
  document.addEventListener('visibilitychange', onVisible);
}

export function acquireSocket(): Promise<Socket | null> {
  refCount += 1;
  cancelPendingClose();
  if (socket) return Promise.resolve(socket);
  if (!connecting) {
    const gen = ++generation;
    const attempt: Promise<Socket | null> = createSocketWithRetry(gen).then((s) => {
      if (!s) {
        if (connecting === attempt) connecting = null;
        return null;
      }
      if (gen !== generation || refCount === 0) {
        s.disconnect();
        if (connecting === attempt) connecting = null;
        return null;
      }
      socket = s;
      return s;
    });
    connecting = attempt;
  }
  return connecting;
}

const CLOSE_DELAY_MS = 5000;
let closeTimer: ReturnType<typeof setTimeout> | null = null;

function cancelPendingClose(): void {
  if (closeTimer != null) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
}

function closeNow(): void {
  closeTimer = null;
  if (refCount > 0) return;
  generation += 1;
  ticketRooms.clear();
  sprintGroupRooms.clear();
  chatRooms.clear();
  connecting = null;
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

export function releaseSocket(): void {
  if (refCount > 0) refCount -= 1;
  if (refCount === 0) {
    cancelPendingClose();
    closeTimer = setTimeout(closeNow, CLOSE_DELAY_MS);
  }
}

export function joinTicketRoom(ticketId: string): () => void {
  const next = (ticketRooms.get(ticketId) ?? 0) + 1;
  ticketRooms.set(ticketId, next);
  if (next === 1 && socket?.connected) socket.emit('join_ticket', ticketId);
  let left = false;
  return () => {
    if (left) return;
    left = true;
    const count = (ticketRooms.get(ticketId) ?? 1) - 1;
    if (count <= 0) {
      ticketRooms.delete(ticketId);
      if (socket?.connected) socket.emit('leave_ticket', ticketId);
    } else {
      ticketRooms.set(ticketId, count);
    }
  };
}

export function joinChatRoom(chatId: string): () => void {
  const next = (chatRooms.get(chatId) ?? 0) + 1;
  chatRooms.set(chatId, next);
  if (next === 1 && socket?.connected) socket.emit('join_chat', chatId);
  let left = false;
  return () => {
    if (left) return;
    left = true;
    const count = (chatRooms.get(chatId) ?? 1) - 1;
    if (count <= 0) {
      chatRooms.delete(chatId);
      if (socket?.connected) socket.emit('leave_chat', chatId);
    } else {
      chatRooms.set(chatId, count);
    }
  };
}

export function joinSprintGroupRoom(groupId: string): () => void {
  const next = (sprintGroupRooms.get(groupId) ?? 0) + 1;
  sprintGroupRooms.set(groupId, next);
  if (next === 1 && socket?.connected) socket.emit('join_sprint_group', groupId);
  let left = false;
  return () => {
    if (left) return;
    left = true;
    const count = (sprintGroupRooms.get(groupId) ?? 1) - 1;
    if (count <= 0) {
      sprintGroupRooms.delete(groupId);
      if (socket?.connected) socket.emit('leave_sprint_group', groupId);
    } else {
      sprintGroupRooms.set(groupId, count);
    }
  };
}
